import { useEffect, useRef, useState } from 'react'

import { useZustand } from '../../hooks/useZustand'
import { usePanelStore } from '../../stores/usePanelStore'
import {
    useOnboardingStore,
    GUIDE_SIGNALS,
} from '../../stores/useOnboardingStore'
import { STAGES, getStageFlow } from './copy'

/**
 * Watches for the real user actions each graph-page stage asks for; the guide
 * never performs one (docs/onboarding/first-session-guide.md). Taps:
 * `forceRequestMode` (every AI trigger converges on it), the graph package's
 * public event bus — never myEventEmitter — and, for the researcher's extend
 * stage, the statements store. The off-graph stages (connect, the builder's
 * bring) live in useOffGraphObservers.
 */

const SUMMARY_REQUEST_MODES = [
    'graph summary',
    'summary',
    'selected graph summary',
]
// 'question' from the Gaps panel, 'develop' / 'context_gap' from the insight overlay.
const GAP_REQUEST_MODES = ['question', 'develop', 'context_gap']
const SUMMARY_BUS_ACTIONS = ['summarize', 'summarize_topics']

const STAGE_ADVANCES_ON = {
    [STAGES.TOPICS]: GUIDE_SIGNALS.TOPIC_SELECTED,
    [STAGES.SUMMARIZE]: GUIDE_SIGNALS.SUMMARY_REQUESTED,
    [STAGES.GAP_VIEW]: GUIDE_SIGNALS.GAPS_VIEW_OPENED,
    [STAGES.GAP]: GUIDE_SIGNALS.GAP_BRIDGE_REQUESTED,
    [STAGES.SAVE_TO_NOTES]: GUIDE_SIGNALS.SAVED_TO_NOTES,
    [STAGES.EXTEND]: GUIDE_SIGNALS.STATEMENTS_ADDED,
}

// Wait for the AI's answer, not just the request.
const STAGES_AWAITING_AI_RESPONSE = new Set([STAGES.SUMMARIZE, STAGES.GAP])

// Time to read the answer before the toast moves on; the first answer gets a little longer.
const RESPONSE_DWELL_MS = {
    [STAGES.SUMMARIZE]: 11000,
    [STAGES.GAP]: 9000,
}

// The gap steps point at controls the AI panel covers: close it (never unmount — it
// still answers the bridge request) as the summary completes, and again on the Gaps click.
const STAGES_CLOSING_AI_PANEL_ON_ADVANCE = new Set([STAGES.SUMMARIZE])
const closeAiPanel = () =>
    usePanelStore.getState().setAiChatOpen(false, { persist: false })

// A beat to see what the click changed before the next instruction.
const STAGE_ADVANCE_DWELL_MS = {
    [STAGES.TOPICS]: 3000,
    [STAGES.GAP_VIEW]: 3000,
    [STAGES.SAVE_TO_NOTES]: 2000,
    [STAGES.EXTEND]: 3000,
}

// Only if no completion ever arrives (old widget build, abandoned request); above a slow answer.
const RESPONSE_FALLBACK_MS = 60000

const countCurrentStatements = state =>
    (state.statements[state.currentContext] || []).length

export default function useGuideObservers({ enabled }) {
    useEffect(() => {
        if (!enabled) return

        const recordSignal = name =>
            useOnboardingStore.getState().recordSignal(name)

        const unsubscribeForceRequestMode = useZustand.subscribe(
            (state, previousState) => {
                const mode = state.forceRequestMode
                if (mode === previousState.forceRequestMode) return
                if (!mode || mode.name === 'none') return
                if (SUMMARY_REQUEST_MODES.includes(mode.name)) {
                    recordSignal(GUIDE_SIGNALS.SUMMARY_REQUESTED)
                }
                if (GAP_REQUEST_MODES.includes(mode.name)) {
                    recordSignal(GUIDE_SIGNALS.GAP_BRIDGE_REQUESTED)
                }
            }
        )

        const handleGraphEvent = event => {
            const { eventName, properties = {} } = event.detail || {}
            if (!eventName) return
            const { action } = properties
            if (
                eventName === 'change_graph_mode' &&
                properties.to_mode === 'gaps'
            ) {
                if (useOnboardingStore.getState().stage === STAGES.GAP_VIEW) {
                    closeAiPanel()
                }
                recordSignal(GUIDE_SIGNALS.GAPS_VIEW_OPENED)
            }
            // Topics row or topic node on the canvas; concept node on the canvas or Concepts menu.
            if (
                (eventName === 'topics_action' && action === 'select_topic') ||
                (eventName === 'graph_action' && action === 'node_click') ||
                (eventName === 'node_action' && action === 'node_click')
            ) {
                recordSignal(GUIDE_SIGNALS.TOPIC_SELECTED)
            }
            // Opening the gap dialog is progress; the step still needs the AI request.
            if (eventName === 'graph_action' && action === 'open_gap_hint') {
                recordSignal(GUIDE_SIGNALS.GAP_HINT_OPENED)
            }
            // Any AI action from the gap connector — the circle (no `modal`) or its dialog. The
            // same dialog serves topic and gateway hints, so `source` is what matters.
            if (
                eventName === 'topics_action' &&
                properties.source === 'gap_connector'
            ) {
                recordSignal(GUIDE_SIGNALS.GAP_BRIDGE_REQUESTED)
            }
            if (
                eventName === 'ai_action' &&
                SUMMARY_BUS_ACTIONS.includes(action)
            ) {
                recordSignal(GUIDE_SIGNALS.SUMMARY_REQUESTED)
            }
        }
        window.addEventListener('infranodus:graph-event', handleGraphEvent)

        return () => {
            unsubscribeForceRequestMode()
            window.removeEventListener(
                'infranodus:graph-event',
                handleGraphEvent
            )
        }
    }, [enabled])

    const stage = useOnboardingStore(state => state.stage)
    const persona = useOnboardingStore(state => state.persona)
    const stageSignalObserved = useOnboardingStore(
        state => !!state.signals[STAGE_ADVANCES_ON[state.stage]]
    )

    // The extend stage: more statements on the open graph than when the stage
    // began. Measured from the loaded graph, never from its initial load — the
    // signal doesn't latch before the stage, since there is nothing earlier to
    // latch from.
    useEffect(() => {
        if (!enabled || stage !== STAGES.EXTEND) return
        const loadedCount = state =>
            state.loadedStatements ? countCurrentStatements(state) : null
        let baseline = loadedCount(useZustand.getState())
        const unsubscribe = useZustand.subscribe(state => {
            const count = loadedCount(state)
            if (count === null) return
            if (baseline === null) {
                baseline = count
                return
            }
            if (count > baseline) {
                useOnboardingStore
                    .getState()
                    .recordSignal(GUIDE_SIGNALS.STATEMENTS_ADDED)
            }
        })
        return unsubscribe
    }, [enabled, stage])

    // 'idle' | 'awaiting' (request sent) | 'answered' (landed; dwelling so it
    // can be read)
    const [aiAnswerState, setAiAnswerState] = useState('idle')

    // Signal already latched when the stage opened: the user acted before being asked.
    const stageEntry = useRef({ stage: null, signalAtEntry: false })
    if (stageEntry.current.stage !== stage) {
        stageEntry.current = { stage, signalAtEntry: stageSignalObserved }
    }

    useEffect(() => {
        if (!enabled || !stageSignalObserved) return

        const advance = advancedBy => {
            if (STAGES_CLOSING_AI_PANEL_ON_ADVANCE.has(stage)) closeAiPanel()
            const flow = getStageFlow(persona)
            const index = flow.indexOf(stage)
            useOnboardingStore
                .getState()
                .advanceTo(flow[Math.min(index + 1, flow.length - 1)], {
                    advancedBy,
                })
        }

        // Never fast-forward an AI step: the next step needs its answer. Ask for a fresh action.
        if (
            STAGES_AWAITING_AI_RESPONSE.has(stage) &&
            stageEntry.current.signalAtEntry
        ) {
            stageEntry.current.signalAtEntry = false
            useOnboardingStore.getState().clearSignal(STAGE_ADVANCES_ON[stage])
            return
        }

        if (!STAGES_AWAITING_AI_RESPONSE.has(stage)) {
            const advancedBy = stageEntry.current.signalAtEntry
                ? 'latched'
                : 'observed'
            const dwell = STAGE_ADVANCE_DWELL_MS[stage]
            if (!dwell) {
                advance(advancedBy)
                return
            }
            const dwellTimer = window.setTimeout(
                () => advance(advancedBy),
                dwell
            )
            return () => window.clearTimeout(dwellTimer)
        }

        // Hold until an answer arrives after this request, then dwell.
        setAiAnswerState('awaiting')
        const tickAtRequest = useOnboardingStore.getState().aiResponseTick
        let dwellTimer = null
        const finish = advancedBy => () => {
            setAiAnswerState('idle')
            advance(advancedBy)
        }
        const unsubscribe = useOnboardingStore.subscribe(state => {
            if (state.aiResponseTick > tickAtRequest && dwellTimer === null) {
                setAiAnswerState('answered')
                dwellTimer = window.setTimeout(
                    finish('ai_response'),
                    RESPONSE_DWELL_MS[stage]
                )
            }
        })
        const fallbackTimer = window.setTimeout(
            finish('ai_timeout'),
            RESPONSE_FALLBACK_MS
        )

        return () => {
            unsubscribe()
            window.clearTimeout(fallbackTimer)
            if (dwellTimer !== null) window.clearTimeout(dwellTimer)
        }
    }, [enabled, stage, persona, stageSignalObserved])

    return { aiAnswerState }
}
